import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronRight, Repeat, CalendarDays, GripVertical } from 'lucide-react';
import { AREE } from '../../constants/areas';
import Checkbox from '../nav/Checkbox';
import PromoteToMitButton from '../nav/PromoteToMitButton';
import DemoteToAzioneButton from '../nav/DemoteToAzioneButton';
import MovedBadge from '../nav/MovedBadge';
import DateAssignPopover from '../nav/DateAssignPopover';
import TaskTextEditor from '../nav/TaskTextEditor';
import { labelGiorno } from '../../lib/periodFormat';

const FADE_TRANS = 'opacity 280ms cubic-bezier(0.22, 1, 0.36, 1)';
const DND_MIME = 'application/x-qp-task';

/**
 * DailyTasksSection — sezione UNIFICATA MIT + Azioni per la Vista Settimana di
 * Pianificazione (`/periodo/settimana/...`).
 *
 * Logica:
 *   - Raggruppa tutti gli elementi (MIT + Azioni) per `data` (ISO YYYY-MM-DD).
 *   - Mostra 7 secchi Lun→Dom (anche vuoti) coi loro task ordinati: prima MIT,
 *     poi Azioni; all'interno per orario/id stabile.
 *   - Le Azioni senza `data` (flottanti, assegnate solo alla settimana) finiscono
 *     in una sezione separata in fondo "Senza giorno · flottanti".
 *   - Ogni riga mostra un badge [MIT] / [AZ] per identificare il tipo.
 *
 * Props:
 *   - sevenDayBuckets : string[] (7 ISO date, Lun→Dom)
 *   - filteredMit, filteredAzioni : task già filtrati (area/stato/conf)
 *   - mit, azioni : interi (per contatori "X di Y")
 *   - filtersActive : bool
 *   - krById
 *   - isCollapsed(kind, key), toggleCollapsed(kind, key)
 *   - onToggleMit(id, next, item), onToggleAzione(id, next, item)
 *   - onChipAreaClick(area)
 *   - onPromote()
 *   - onEditRecurrence(parent)
 *   - onAssignDate(azId, currentVal, newVal)
 *   - onReassignTask(kind, id, currentData, newData|null) — drag&drop handler.
 *     kind ∈ {'mit','azione'}. newData null = drop su sezione "Senza giorno".
 */
export default function DailyTasksSection({
  sevenDayBuckets,
  filteredMit, filteredAzioni,
  mit, azioni, filtersActive,
  krById,
  isCollapsed, toggleCollapsed,
  onToggleMit, onToggleAzione,
  onChipAreaClick,
  onPromote, onEditRecurrence, onAssignDate,
  onReassignTask,
  onUpdateText, onDeleteTask, onDemoted,
}) {
  const [dragOverKey, setDragOverKey] = useState(null);
  const [draggingId, setDraggingId] = useState(null);

  const handleDragStart = (e, kind, id, currentData) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData(DND_MIME, JSON.stringify({ kind, id, currentData }));
    setDraggingId(id);
  };
  const handleDragEnd = () => {
    setDraggingId(null);
    setDragOverKey(null);
  };
  const handleDayDrop = async (e, targetKey) => {
    e.preventDefault();
    setDragOverKey(null);
    const payload = e.dataTransfer.getData(DND_MIME);
    if (!payload || !onReassignTask) return;
    try {
      const { kind, id, currentData } = JSON.parse(payload);
      // targetKey: 'YYYY-MM-DD' per un giorno, null per "Senza giorno"
      if (targetKey && currentData === targetKey) return;
      if (!targetKey && !currentData) return;
      await onReassignTask(kind, id, currentData, targetKey);
    } catch (err) {
      // Payload di drag non valido o errore di parse: log ma non blocca l'UX
      console.warn('DailyTasksSection: drop payload non valido', err?.message || err);
    }
  };
  // Mappa giorno → { mit:[], azioni:[] }
  const byDay = useMemo(() => {
    const m = new Map();
    (sevenDayBuckets || []).forEach((iso) => m.set(iso, { mit: [], azioni: [] }));
    for (const x of filteredMit) {
      if (!x.data) continue;
      if (!m.has(x.data)) m.set(x.data, { mit: [], azioni: [] });
      m.get(x.data).mit.push(x);
    }
    for (const a of filteredAzioni) {
      if (!a.data) continue;
      if (!m.has(a.data)) m.set(a.data, { mit: [], azioni: [] });
      m.get(a.data).azioni.push(a);
    }
    return m;
  }, [sevenDayBuckets, filteredMit, filteredAzioni]);

  // Azioni "flottanti" (senza data, solo settimana)
  const floatingAzioni = useMemo(
    () => filteredAzioni.filter((a) => !a.data),
    [filteredAzioni],
  );
  // MIT senza data (caso raro ma possibile)
  const floatingMit = useMemo(
    () => filteredMit.filter((m) => !m.data),
    [filteredMit],
  );

  const totalCount = filteredMit.length + filteredAzioni.length;
  const totalRaw = mit.length + azioni.length;
  const doneCount = filteredMit.filter((x) => x.fatto).length + filteredAzioni.filter((x) => x.fatto).length;

  // Toggle "Mostra fatte" — default OFF, così le task chiuse non confondono
  // la vista operativa. Persistito in localStorage per rispettare la scelta
  // dell'utente tra un caricamento e l'altro (chiave 'qp:show-done-week').
  const [showDone, setShowDone] = useState(() => {
    try { return localStorage.getItem('qp:show-done-week') === '1'; } catch { return false; }
  });
  const toggleShowDone = () => {
    setShowDone((prev) => {
      const next = !prev;
      try { localStorage.setItem('qp:show-done-week', next ? '1' : '0'); } catch { /* quota o SSR */ }
      return next;
    });
  };
  const isVisible = (x) => showDone || !x.fatto;

  // Anche se non ci sono task mostriamo i giorni come placeholder per la pianificazione
  return (
    <section className="mb-12" data-testid="section-daily-tasks">
      <div className="flex items-baseline justify-between gap-4 mb-4 flex-wrap">
        <h2 className="font-display text-xl md:text-2xl tracking-tight">
          Task della settimana · {totalCount}
          {filtersActive && totalRaw > totalCount ? ` di ${totalRaw}` : ''}
        </h2>
        <div className="flex items-center gap-4">
          {doneCount > 0 && (
            <button
              type="button"
              onClick={toggleShowDone}
              className="font-mono text-[10px] uppercase tracking-[0.15em] text-mute hover:text-ink transition-colors"
              data-testid="week-toggle-show-done"
              title={showDone
                ? 'Nascondi le task già completate per focalizzare sul rimanente'
                : `Mostra anche le ${doneCount} task già completate`}
            >
              {showDone ? '✓ ' : ''}Fatte · {doneCount} {showDone ? '(nascondi)' : '(mostra)'}
            </button>
          )}
          <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-mute">
            Trascina i task tra i giorni per spostarli
          </span>
        </div>
      </div>

      {(sevenDayBuckets || []).map((iso) => {
        const bucket = byDay.get(iso) || { mit: [], azioni: [] };
        const items = [...bucket.mit, ...bucket.azioni];
        const done = items.filter((x) => x.fatto).length;
        const pct = items.length > 0 ? Math.round((done / items.length) * 100) : 0;
        const headerLabel = labelGiorno(iso);
        const collapsed = isCollapsed('daily', iso);
        const isDropTarget = dragOverKey === iso;

        return (
          <div
            key={iso}
            className="mb-5 transition-colors"
            data-testid={`daily-group-${iso}`}
            style={{
              borderTop: isDropTarget ? '2px solid var(--qp-success)' : '1px solid var(--qp-border)',
              paddingTop: 10,
              backgroundColor: isDropTarget ? 'rgb(var(--qp-success-rgb) / 0.06)' : undefined,
            }}
            onDragOver={(e) => {
              if (!e.dataTransfer.types.includes(DND_MIME)) return;
              e.preventDefault();
              e.dataTransfer.dropEffect = 'move';
              setDragOverKey(iso);
            }}
            onDragLeave={() => setDragOverKey((k) => (k === iso ? null : k))}
            onDrop={(e) => handleDayDrop(e, iso)}
          >
            <button
              type="button"
              onClick={() => toggleCollapsed('daily', iso)}
              className="w-full flex items-baseline gap-3 mb-2 pb-1 border-b border-line hover:bg-linesoft/40 transition-colors text-left"
              data-testid={`daily-group-header-${iso}`}
              aria-expanded={!collapsed}
            >
              <span className="shrink-0 text-mute" aria-hidden>
                {collapsed
                  ? <ChevronRight size={12} strokeWidth={1.5} />
                  : <ChevronDown size={12} strokeWidth={1.5} />}
              </span>
              <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-ink">
                {headerLabel}
              </span>
              <span className="font-mono text-[10px] text-soft tabular-nums" data-testid={`daily-group-stat-${iso}`}>
                {done}/{items.length}{items.length > 0 ? ` · ${pct}%` : ''}
              </span>
              {items.length > 0 && (
                <span className="flex-1 h-1 bg-linesoft relative overflow-hidden ml-1" aria-hidden="true">
                  <span
                    className="absolute inset-y-0 left-0 transition-all"
                    style={{ width: `${pct}%`, backgroundColor: pct === 100 ? 'var(--qp-success)' : 'var(--qp-ink)' }}
                  />
                </span>
              )}
            </button>
            {!collapsed && (items.length === 0 ? (
              <p className="font-body text-xs text-mute italic px-1 py-2" data-testid={`daily-empty-${iso}`}>
                — nessun task per questo giorno —
              </p>
            ) : (
              <ul className="divide-y divide-linesoft">
                {bucket.mit.filter(isVisible).map((m) => (
                  <MitRow
                    key={`mit:${m.id}`}
                    item={m}
                    kr={m.key_result_id ? krById[m.key_result_id] : null}
                    onToggle={onToggleMit}
                    onChipAreaClick={onChipAreaClick}
                    onEditRecurrence={onEditRecurrence}
                    onAssignDate={onAssignDate}
                    onUpdateText={onUpdateText}
                    onDeleteTask={onDeleteTask}
                    onDemoted={onDemoted}
                    draggable={!!onReassignTask}
                    dragging={draggingId === m.id}
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}
                  />
                ))}
                {bucket.azioni.filter(isVisible).map((a) => (
                  <AzioneRow
                    key={`az:${a.id}`}
                    item={a}
                    kr={a.key_result_id ? krById[a.key_result_id] : null}
                    onToggle={onToggleAzione}
                    onChipAreaClick={onChipAreaClick}
                    onEditRecurrence={onEditRecurrence}
                    onAssignDate={onAssignDate}
                    onPromote={onPromote}
                    onUpdateText={onUpdateText}
                    onDeleteTask={onDeleteTask}
                    draggable={!!onReassignTask}
                    dragging={draggingId === a.id}
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}
                  />
                ))}
              </ul>
            ))}
          </div>
        );
      })}

      {/* Sezione "Senza giorno · flottanti" — azioni assegnate solo alla settimana */}
      <FloatingGroup
        floatingMit={floatingMit.filter(isVisible)}
        floatingAzioni={floatingAzioni.filter(isVisible)}
        krById={krById}
        collapsed={isCollapsed('daily', 'senza-giorno')}
        onToggleCollapse={() => toggleCollapsed('daily', 'senza-giorno')}
        onToggleMit={onToggleMit}
        onToggleAzione={onToggleAzione}
        onChipAreaClick={onChipAreaClick}
        onEditRecurrence={onEditRecurrence}
        onAssignDate={onAssignDate}
        onPromote={onPromote}
        onReassignTask={onReassignTask}
        onUpdateText={onUpdateText}
        onDeleteTask={onDeleteTask}
        dragOverKey={dragOverKey}
        setDragOverKey={setDragOverKey}
        handleDayDrop={handleDayDrop}
        draggingId={draggingId}
        handleDragStart={handleDragStart}
        handleDragEnd={handleDragEnd}
      />
    </section>
  );
}

function FloatingGroup({
  floatingMit, floatingAzioni, krById,
  collapsed, onToggleCollapse,
  onToggleMit, onToggleAzione,
  onChipAreaClick, onEditRecurrence, onAssignDate, onPromote,
  onReassignTask, dragOverKey, setDragOverKey, handleDayDrop,
  draggingId, handleDragStart, handleDragEnd,
  onUpdateText, onDeleteTask,
}) {
  const items = [...floatingMit, ...floatingAzioni];
  const done = items.filter((x) => x.fatto).length;
  const pct = items.length > 0 ? Math.round((done / items.length) * 100) : 0;
  const isDropTarget = dragOverKey === 'senza-giorno';

  // Quando non c'è drag in corso e la sezione è vuota, la nascondiamo come prima.
  // Quando c'è drag attivo (draggingId), la mostriamo SEMPRE come drop target
  // per permettere di "scaricare" un task ed averlo flottante.
  const showSection = items.length > 0 || (draggingId && onReassignTask);
  if (!showSection) return null;

  return (
    <div
      className="mb-5 transition-colors"
      data-testid="daily-group-senza-giorno"
      style={{
        borderTop: isDropTarget ? '2px solid var(--qp-success)' : '2px dashed var(--qp-border)',
        paddingTop: 10,
        backgroundColor: isDropTarget ? 'rgb(var(--qp-success-rgb) / 0.06)' : undefined,
      }}
      onDragOver={(e) => {
        if (!e.dataTransfer.types.includes(DND_MIME)) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        setDragOverKey('senza-giorno');
      }}
      onDragLeave={() => setDragOverKey((k) => (k === 'senza-giorno' ? null : k))}
      onDrop={(e) => handleDayDrop(e, null)}
    >
      <button
        type="button"
        onClick={onToggleCollapse}
        className="w-full flex items-baseline gap-3 mb-2 pb-1 border-b border-line hover:bg-linesoft/40 transition-colors text-left"
        data-testid="daily-group-header-senza-giorno"
        aria-expanded={!collapsed}
      >
        <span className="shrink-0 text-mute" aria-hidden>
          {collapsed
            ? <ChevronRight size={12} strokeWidth={1.5} />
            : <ChevronDown size={12} strokeWidth={1.5} />}
        </span>
        <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-soft">
          Senza giorno · flottanti
        </span>
        <span className="font-mono text-[10px] text-soft tabular-nums">
          {done}/{items.length}{items.length > 0 ? ` · ${pct}%` : ''}
        </span>
        <span className="flex-1 h-1 bg-linesoft relative overflow-hidden ml-1" aria-hidden="true">
          <span
            className="absolute inset-y-0 left-0 transition-all"
            style={{ width: `${pct}%`, backgroundColor: pct === 100 ? 'var(--qp-success)' : 'var(--qp-ink)' }}
          />
        </span>
      </button>
      {!collapsed && (items.length === 0 ? (
        <p className="font-body text-xs text-mute italic px-1 py-2">
          {isDropTarget ? 'Rilascia qui per rendere flottante' : '— rilascia qui per rendere il task flottante (solo settimana) —'}
        </p>
      ) : (
        <ul className="divide-y divide-linesoft">
          {floatingMit.map((m) => (
            <MitRow
              key={`fmit:${m.id}`}
              item={m}
              kr={m.key_result_id ? krById[m.key_result_id] : null}
              onToggle={onToggleMit}
              onChipAreaClick={onChipAreaClick}
              onEditRecurrence={onEditRecurrence}
              onAssignDate={onAssignDate}
              onUpdateText={onUpdateText}
              onDeleteTask={onDeleteTask}
              onDemoted={onDemoted}
              draggable={!!onReassignTask}
              dragging={draggingId === m.id}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
            />
          ))}
          {floatingAzioni.map((a) => (
            <AzioneRow
              key={`faz:${a.id}`}
              item={a}
              kr={a.key_result_id ? krById[a.key_result_id] : null}
              onToggle={onToggleAzione}
              onChipAreaClick={onChipAreaClick}
              onEditRecurrence={onEditRecurrence}
              onAssignDate={onAssignDate}
              onPromote={onPromote}
              onUpdateText={onUpdateText}
              onDeleteTask={onDeleteTask}
              draggable={!!onReassignTask}
              dragging={draggingId === a.id}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
            />
          ))}
        </ul>
      ))}
    </div>
  );
}

function TypeBadge({ kind }) {
  const isMit = kind === 'mit';
  return (
    <span
      className={`font-mono text-[9px] uppercase tracking-[0.18em] shrink-0 px-1.5 py-0.5 ${
        isMit
          ? 'text-canvas bg-ink border border-ink'
          : 'text-soft bg-canvas border border-mute'
      }`}
      aria-label={isMit ? 'MIT' : 'Azione'}
      title={isMit ? 'Most Important Task' : 'Azione settimanale'}
    >
      {isMit ? 'MIT' : 'AZ'}
    </span>
  );
}

/**
 * RinviiBadge — mostra il contatore di rinvii SOLO se ≥ 3 (soglia zombie).
 * Rende visibile all'utente che una task è stata spostata troppe volte,
 * suggerendo di eliminarla o completarla davvero. Silenzioso sotto la soglia
 * per non aggiungere rumore visivo.
 */
function RinviiBadge({ count }) {
  const n = Number(count) || 0;
  if (n < 3) return null;
  return (
    <span
      className="font-mono text-[9px] uppercase tracking-[0.18em] shrink-0 px-1.5 py-0.5 border border-accent text-accent bg-accent/5"
      aria-label={`Rinviata ${n} volte`}
      title={`Rinviata ${n} volte — probabilmente non è così importante, considera di eliminarla o farla ora`}
      data-testid={`rinvii-badge-${n}`}
    >
      ↻ {n}
    </span>
  );
}

function MitRow({
  item: m, kr, onToggle, onChipAreaClick, onEditRecurrence, onAssignDate,
  onUpdateText, onDeleteTask, onDemoted,
  draggable, dragging, onDragStart, onDragEnd,
}) {
  const areaKey = (kr?.area || '').toLowerCase();
  const meta = AREE[areaKey] || null;
  const textStyle = {
    textDecoration: m.fatto ? 'line-through' : 'none',
    color: m.fatto ? 'var(--qp-text-mute)' : undefined,
    opacity: m.fatto ? 0.5 : 1,
    transition: FADE_TRANS,
  };
  return (
    <li
      className={`group py-3 flex items-center gap-3 ${dragging ? 'opacity-40' : ''}`}
      data-testid={`row-mit-${m.id}`}
      draggable={!!draggable && !m.is_occurrence}
      onDragStart={(e) => onDragStart && onDragStart(e, 'mit', m.id, m.data)}
      onDragEnd={onDragEnd}
    >
      {draggable && !m.is_occurrence && (
        <span
          className="shrink-0 text-mute cursor-grab active:cursor-grabbing hover:text-ink transition-colors"
          title="Trascina per spostare in un altro giorno"
          aria-hidden
        >
          <GripVertical size={14} strokeWidth={1.5} />
        </span>
      )}
      <Checkbox
        checked={m.fatto}
        onToggle={(next) => onToggle(m.id, next, m)}
        size={18}
        label={m.testo}
        testid={`toggle-mit-${m.id}`}
      />
      <MovedBadge id={m.id} testid={`moved-badge-mit-${m.id}`} />
      <TypeBadge kind="mit" />
      <RinviiBadge count={m.rinvii} />
      {(m.recurrence_rule || m.is_occurrence) ? (
        <button
          type="button"
          onClick={() => onEditRecurrence && onEditRecurrence({
            id: m.parent_id || m.id.split('::')[0],
            kind: 'mit',
            testo: m.testo,
            recurrence_rule: m.recurrence_rule,
            recurrence_end: m.recurrence_end,
          })}
          className="shrink-0 inline-flex items-center justify-center w-5 h-5 border border-ink bg-ink text-canvas hover:bg-ok hover:border-ok transition-colors"
          title={m.is_occurrence ? 'Occorrenza ricorrente · modifica serie' : 'Modifica ricorrenza'}
          aria-label="Modifica ricorrenza"
          data-testid={`mit-recurrence-btn-${m.id}`}
        >
          <Repeat size={10} strokeWidth={1.8} />
        </button>
      ) : !m.is_occurrence && (
        <button
          type="button"
          onClick={() => onEditRecurrence && onEditRecurrence({
            id: m.id,
            kind: 'mit',
            testo: m.testo,
            recurrence_rule: null,
            recurrence_end: null,
          })}
          className="shrink-0 inline-flex items-center justify-center w-5 h-5 border border-transparent text-mute hover:border-line hover:text-ink opacity-0 group-hover:opacity-100 transition-all"
          title="Rendi ricorrente"
          aria-label="Rendi ricorrente"
          data-testid={`mit-recurrence-add-${m.id}`}
        >
          <Repeat size={10} strokeWidth={1.5} />
        </button>
      )}
      {meta && (
        <button
          type="button"
          onClick={() => onChipAreaClick(areaKey)}
          className="font-mono text-[10px] uppercase tracking-[0.15em] px-1.5 py-0.5 border shrink-0 underline decoration-dotted underline-offset-2 hover:no-underline hover:opacity-70 cursor-pointer"
          style={{ borderColor: meta.color, color: meta.color }}
          title={`Filtra per ${meta.label}`}
          data-testid={`periodo-mit-area-chip-${m.id}`}
        >
          {meta.label.slice(0, 3)}
        </button>
      )}
      {onUpdateText ? (
        <TaskTextEditor
          kind="mit"
          id={m.id}
          isOccurrence={!!m.is_occurrence}
          text={m.testo}
          onSaveText={(nv) => onUpdateText('mit', m.id, nv)}
          onDelete={onDeleteTask ? () => onDeleteTask('mit', m.id) : null}
          textStyle={textStyle}
        />
      ) : (
        <span
          className="flex-1 font-body text-sm"
          style={textStyle}
        >
          {m.testo}
        </span>
      )}
      {kr && (
        <Link
          to={`/obiettivo/${kr.obiettivo_id}`}
          className="font-mono text-[10px] uppercase tracking-[0.15em] text-soft hover:text-accent shrink-0"
          data-testid={`mit-kr-link-${m.id}`}
        >
          → {kr.titolo}
        </Link>
      )}
      {onAssignDate && !m.is_occurrence && (
        <DateAssignPopover
          kind="mit"
          currentValue={m.data || ''}
          onSave={(nv) => onAssignDate(m.id, m.data || '', nv, 'mit')}
          side="left"
          testid={`mit-date-popover-${m.id}`}
        >
          <button
            type="button"
            className={`shrink-0 inline-flex items-center justify-center w-5 h-5 border transition-colors ${
              m.data
                ? 'border-ink text-ink hover:bg-ink hover:text-canvas'
                : 'border-line text-mute hover:border-ink hover:text-ink'
            }`}
            title={m.data
              ? `MIT fissata al ${m.data} · clicca per cambiare data`
              : 'Sposta MIT a un altro giorno'}
            aria-label="Sposta MIT"
            data-testid={`mit-date-btn-${m.id}`}
          >
            <CalendarDays size={11} strokeWidth={1.6} />
          </button>
        </DateAssignPopover>
      )}
      {onDemoted && !m.is_occurrence && (
        <DemoteToAzioneButton
          mitId={m.id}
          onDemoted={onDemoted}
          testid={`daily-demote-azione-${m.id}`}
        />
      )}
    </li>
  );
}

function AzioneRow({
  item: a, kr, onToggle, onChipAreaClick, onEditRecurrence, onAssignDate, onPromote,
  onUpdateText, onDeleteTask,
  draggable, dragging, onDragStart, onDragEnd,
}) {
  const areaKey = (a.area || kr?.area || '').toLowerCase();
  const meta = AREE[areaKey] || { color: 'var(--qp-text-soft)', label: '—' };
  const textStyle = {
    textDecoration: a.fatto ? 'line-through' : 'none',
    color: a.fatto ? 'var(--qp-text-mute)' : undefined,
    opacity: a.fatto ? 0.5 : 1,
    transition: FADE_TRANS,
  };
  return (
    <li
      className={`group py-3 flex items-center gap-3 ${dragging ? 'opacity-40' : ''}`}
      data-testid={`row-azione-${a.id}`}
      draggable={!!draggable && !a.is_occurrence}
      onDragStart={(e) => onDragStart && onDragStart(e, 'azione', a.id, a.data)}
      onDragEnd={onDragEnd}
    >
      {draggable && !a.is_occurrence && (
        <span
          className="shrink-0 text-mute cursor-grab active:cursor-grabbing hover:text-ink transition-colors"
          title="Trascina per spostare in un altro giorno o renderla flottante"
          aria-hidden
        >
          <GripVertical size={14} strokeWidth={1.5} />
        </span>
      )}
      <Checkbox
        checked={a.fatto}
        onToggle={(next) => onToggle(a.id, next, a)}
        size={18}
        label={a.testo}
        testid={`toggle-azione-${a.id}`}
      />
      <TypeBadge kind="azione" />
      <RinviiBadge count={a.rinvii} />
      <MovedBadge id={a.id} testid={`moved-badge-azione-${a.id}`} />
      {(a.recurrence_rule || a.is_occurrence) ? (
        <button
          type="button"
          onClick={() => onEditRecurrence && onEditRecurrence({
            id: a.parent_id || a.id.split('::')[0],
            kind: 'azione',
            testo: a.testo,
            recurrence_rule: a.recurrence_rule,
            recurrence_end: a.recurrence_end,
          })}
          className="shrink-0 inline-flex items-center justify-center w-5 h-5 border border-ink bg-ink text-canvas hover:bg-ok hover:border-ok transition-colors"
          title={a.is_occurrence ? 'Occorrenza ricorrente · modifica serie' : 'Modifica ricorrenza'}
          aria-label="Modifica ricorrenza"
          data-testid={`azione-recurrence-btn-${a.id}`}
        >
          <Repeat size={10} strokeWidth={1.8} />
        </button>
      ) : (
        <button
          type="button"
          onClick={() => onEditRecurrence && onEditRecurrence({
            id: a.id,
            kind: 'azione',
            testo: a.testo,
            recurrence_rule: null,
            recurrence_end: null,
          })}
          className="shrink-0 inline-flex items-center justify-center w-5 h-5 border border-transparent text-mute hover:border-line hover:text-ink opacity-0 group-hover:opacity-100 transition-all"
          title="Rendi ricorrente"
          aria-label="Rendi ricorrente"
          data-testid={`azione-recurrence-add-${a.id}`}
        >
          <Repeat size={10} strokeWidth={1.5} />
        </button>
      )}
      {areaKey && (
        <button
          type="button"
          onClick={() => onChipAreaClick(areaKey)}
          className="font-mono text-[10px] uppercase tracking-[0.15em] px-1.5 py-0.5 border shrink-0 underline decoration-dotted underline-offset-2 hover:no-underline hover:opacity-70 cursor-pointer"
          style={{ borderColor: meta.color, color: meta.color }}
          title={`Filtra per ${meta.label}`}
          data-testid={`periodo-azione-area-chip-${a.id}`}
        >
          {a.is_bau ? 'BAU' : (meta.label || a.area || '—').slice(0, 3)}
        </button>
      )}
      {onUpdateText ? (
        <TaskTextEditor
          kind="azione"
          id={a.id}
          isOccurrence={!!a.is_occurrence}
          text={a.testo}
          onSaveText={(nv) => onUpdateText('azione', a.id, nv)}
          onDelete={onDeleteTask ? () => onDeleteTask('azione', a.id) : null}
          textStyle={textStyle}
        />
      ) : (
        <span
          className="flex-1 font-body text-sm"
          style={textStyle}
        >
          {a.testo}
        </span>
      )}
      {kr && (
        <Link
          to={`/obiettivo/${kr.obiettivo_id}`}
          className="font-mono text-[10px] uppercase tracking-[0.15em] text-soft hover:text-accent shrink-0"
          data-testid={`azione-kr-link-${a.id}`}
        >
          → {kr.titolo}
        </Link>
      )}
      {/* Pillola data: visibile solo per azioni flottanti (senza data) → mostra W */}
      {!a.data && a.settimana && (
        <span
          className="font-mono text-[10px] tabular-nums shrink-0 px-1.5 py-0.5 border border-line text-mute"
          title={`Settimana ${a.settimana}`}
          data-testid={`azione-date-label-${a.id}`}
        >
          {(a.settimana || '').replace('20', '').replace('-W', ' · W')}
        </span>
      )}
      {onAssignDate && !a.is_occurrence && (
        <DateAssignPopover
          kind="azione"
          currentValue={a.data || a.settimana || ''}
          onSave={(nv) => onAssignDate(a.id, a.data || a.settimana || '', nv)}
          side="left"
          testid={`azione-date-popover-${a.id}`}
        >
          <button
            type="button"
            className={`shrink-0 inline-flex items-center justify-center w-5 h-5 border transition-colors ${
              a.data
                ? 'border-ink text-ink hover:bg-ink hover:text-canvas'
                : 'border-line text-mute hover:border-ink hover:text-ink'
            }`}
            title={a.data
              ? `Azione fissata al ${a.data} · clicca per cambiare`
              : 'Assegna data specifica o sposta settimana'}
            aria-label="Assegna data"
            data-testid={`azione-date-btn-${a.id}`}
          >
            <CalendarDays size={11} strokeWidth={1.6} />
          </button>
        </DateAssignPopover>
      )}
      <PromoteToMitButton
        azioneId={a.id}
        onPromoted={onPromote}
        testid={`periodo-promote-mit-${a.id}`}
      />
    </li>
  );
}
